import React from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Box, Chip, Typography, IconButton } from "@mui/material";
import { DragIndicator as DragIndicatorIcon, Close as CloseIcon } from "@mui/icons-material";

const SortableItem = ({
  id,
  item,
  index,
  disabled,
  showRemoveButton,
  chipColor,
  customStyles,
  renderItem,
  onRemove,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        padding: "12px",
        marginBottom: "12px",
        backgroundColor: isDragging ? "#e3f2fd" : "#fff",
        borderRadius: "8px",
        border: isDragging ? "2px solid #1976d2" : "1px solid #e0e0e0",
        boxShadow: isDragging 
          ? "0 8px 16px rgba(0,0,0,0.2)" 
          : "0 1px 3px rgba(0,0,0,0.05)",
        transition: isDragging 
          ? "box-shadow 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)" 
          : "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        cursor: disabled ? "default" : "move",
        opacity: disabled ? 0.6 : 1,
        willChange: "transform, box-shadow, border-color",
        "&:hover": {
          borderColor: disabled ? "#e0e0e0" : "#1976d2",
          boxShadow: disabled 
            ? "0 1px 3px rgba(0,0,0,0.05)" 
            : "0 4px 12px rgba(25, 118, 210, 0.15)",
          transform: disabled ? "none" : "translateY(-2px)",
        },
        "&:last-child": {
          marginBottom: 0,
        },
        ...customStyles.item,
      }}
    >
      {/* Drag Handle */}
      <Box
        {...attributes}
        {...listeners}
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: disabled ? "default" : "move",
          color: disabled ? "#bbb" : "#000",
          touchAction: "none",
          transition: "color 0.2s ease",
          "&:hover": {
            color: disabled ? "#bbb" : "#1976d2",
          },
          "&:active": {
            cursor: disabled ? "default" : "grabbing",
            color: disabled ? "#bbb" : "#1976d2",
          },
          ...customStyles.dragHandle,
        }}
      >
        <DragIndicatorIcon />
      </Box>

      {/* Priority Chip */}
      <Chip
        label={index + 1}
        size="small"
        sx={{
          backgroundColor: chipColor.backgroundColor,
          color: chipColor.color,
          fontWeight: 600,
          minWidth: "32px",
          fontSize: "12px",
        }}
      />

      {/* Item Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {renderItem ? (
          renderItem(item, index)
        ) : (
          <>
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 600,
                color: "#1976d2",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {item.title}
            </Typography>
            {item.subtitle && (
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "#666",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {item.subtitle}
              </Typography>
            )}
          </>
        )}
      </Box>

      {/* Remove Button */}
      {showRemoveButton && !disabled && (
        <IconButton
          size="small"
          color="error"
          onClick={() => onRemove(item.id)}
          sx={{
            opacity: 0.7,
            "&:hover": {
              opacity: 1,
              backgroundColor: "rgba(211, 47, 47, 0.08)",
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};

const DraggableList = ({
  items = [],
  onReorder,
  onRemove,
  showRemoveButton = true,
  disabled = false,
  emptyMessage = "No items available",
  chipColor = {
    backgroundColor: "#1976d2",
    color: "#fff",
  },
  customStyles = {},
  renderItem,
}) => {
  // Configure sensors for drag detection
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // Reduced for smoother drag activation
        tolerance: 5,
        delay: 100,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  /**
   * Handles the end of a drag operation
   * Reorders the items array and calls onReorder callback
   */
  const handleDragEnd = (event) => {
    const { active, over } = event;

    // Don't do anything if dropped outside the list or disabled
    if (!over || disabled || active.id === over.id) return;

    const oldIndex = items.findIndex((item) => item.id === active.id);
    const newIndex = items.findIndex((item) => item.id === over.id);

    // Reorder the items array
    const newItems = arrayMove(items, oldIndex, newIndex);

    // Call the onReorder callback with new items
    if (onReorder) {
      onReorder(newItems);
    }
  };

  /**
   * Handles remove button click
   */
  const handleRemove = (itemId) => {
    if (onRemove) {
      onRemove(itemId);
    }
  };

  // Show empty state if no items
  if (items.length === 0) {
    return (
      <Box
        sx={{
          textAlign: "center",
          py: 4,
          color: "text.secondary",
          backgroundColor: "#f9f9f9",
          borderRadius: "8px",
          border: "1px dashed #ddd",
          ...customStyles.container,
        }}
      >
        <Typography variant="body2">{emptyMessage}</Typography>
      </Box>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={items.map((item) => item.id)}
        strategy={verticalListSortingStrategy}
      >
        <Box
          sx={{
            ...customStyles.container,
          }}
        >
          {items.map((item, index) => (
            <SortableItem
              key={item.id}
              id={item.id}
              item={item}
              index={index}
              disabled={disabled}
              showRemoveButton={showRemoveButton}
              chipColor={chipColor}
              customStyles={customStyles}
              renderItem={renderItem}
              onRemove={handleRemove}
            />
          ))}
        </Box>
      </SortableContext>
    </DndContext>
  );
};

export default DraggableList;
